import React, { useEffect, useState } from 'react';
import { Phone } from 'lucide-react';

const HERO_IMAGES = [
  // Warm amber LED cove architectural lighting — perfect smart lighting showcase
  'https://images.unsplash.com/photo-1560257084-d568f6eb7b29?w=1920&q=85',
  // Modern TV wall with RGB LED bias lighting
  'https://images.unsplash.com/photo-1577979749830-f1d742b96791?w=1920&q=85',
  // Luxury warm-lit living room at night
  'https://images.unsplash.com/photo-1564078516393-cf04bd966897?w=1920&q=85',
];

const Hero = ({ onBook, onQuote, onCall }) => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIdx((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="home"
      className="relative pt-[92px] min-h-[760px] bg-[#0B1430] overflow-hidden"
    >
      {/* Background image crossfade stack */}
      {HERO_IMAGES.map((src, i) => (
        <div
          key={src}
          className={`absolute inset-0 bg-cover bg-center kenburns transition-opacity duration-[1600ms] ease-in-out ${
            i === idx ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ backgroundImage: `url('${src}')` }}
        />
      ))}
      {/* Warm accent glow (mimics automated lighting effect) */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{
          background:
            'radial-gradient(ellipse at 78% 60%, rgba(212,168,83,0.28) 0%, rgba(212,168,83,0) 55%), radial-gradient(ellipse at 15% 30%, rgba(212,168,83,0.14) 0%, rgba(212,168,83,0) 45%)',
        }}
      />
      <div className="absolute inset-0 hero-overlay" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
        {/* Left content */}
        <div className="text-white">
          <div className="hero-rise inline-block px-4 py-1.5 border border-[#D4A853]/60 rounded-full text-[11px] tracking-[0.25em] text-[#D4A853] font-semibold mb-7">
            SMARTER HOME. BRIGHTER LIFE.
          </div>
          <h1 className="hero-rise hero-rise-delay-1 text-4xl md:text-5xl lg:text-[56px] leading-[1.08] font-bold mb-6">
            Smart Home Lighting,
            <br />
            <span className="font-semibold">Controlled by Your Voice</span>
          </h1>
          <p className="hero-rise hero-rise-delay-2 text-white/80 text-[16px] leading-relaxed max-w-xl mb-8">
            Seamlessly automate your home’s lighting with Alexa and Siri integration. Professional installation and dedicated after-sales support.
          </p>

          {/* Voice integrations */}
          <div className="hero-rise hero-rise-delay-3 flex flex-wrap gap-3 mb-8">
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-md bg-white/5 border border-white/15 backdrop-blur-sm">
              <span className="text-[10px] text-white/60 leading-tight">works<br/>with</span>
              <span className="text-white font-semibold italic">alexa</span>
            </div>
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-md bg-white/5 border border-white/15 backdrop-blur-sm">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400" />
              <div className="leading-tight">
                <div className="text-[10px] text-white/60">Works with</div>
                <div className="text-white font-semibold text-sm">Apple Siri</div>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="hero-rise hero-rise-delay-4 flex flex-wrap gap-3">
            <button
              onClick={onBook}
              className="px-6 py-3.5 rounded-md bg-[#D4A853] hover:bg-[#B8893A] text-[#0B1430] font-semibold text-[14px] transition-colors duration-300"
            >
              Book Free Consultation
            </button>
            <button
              onClick={onQuote}
              className="px-6 py-3.5 rounded-md border border-white/40 hover:border-[#D4A853] hover:text-[#D4A853] text-white font-semibold text-[14px] transition-colors duration-300"
            >
              Get a Quote
            </button>
            <button
              onClick={onCall}
              className="px-5 py-3.5 rounded-md border border-white/40 hover:border-[#D4A853] hover:text-[#D4A853] text-white font-semibold text-[14px] inline-flex items-center gap-2 transition-colors duration-300"
            >
              <Phone size={16} /> Call Now
            </button>
          </div>
        </div>

        {/* Right visuals - voice bubble + phone mock */}
        <div className="hero-rise hero-rise-delay-3 relative h-[480px] hidden lg:block">
          {/* Voice bubble */}
          <div className="absolute top-2 right-6 voice-bubble max-w-[280px] text-[14px] leading-snug">
            “Alexa, turn on<br/>the living room lights.”
          </div>

          {/* Phone mock */}
          <div className="absolute right-2 bottom-0 w-[240px] phone-mock p-3">
            <div className="bg-[#0B1430] rounded-[28px] overflow-hidden border border-white/10">
              <div className="flex justify-between items-center px-5 pt-4 text-white text-xs">
                <span>9:41</span>
                <span className="text-white/70">•••</span>
              </div>
              <div className="px-5 pt-5 pb-2">
                <div className="text-white text-sm">Living Room</div>
                <div className="text-white text-3xl font-light mt-1">100%</div>
              </div>
              <div className="flex justify-center py-4">
                <div className="w-32 h-32 rounded-full border-[6px] border-[#D4A853] relative">
                  <div className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-[#D4A853]/20 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full bg-[#D4A853]/40" />
                  </div>
                </div>
              </div>
              <div className="px-4 pb-5">
                <div className="text-white/70 text-[11px] mb-2">Scenes</div>
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  {['Movie Mode','Dinner Mode','Party Mode','Good Night'].map((s) => (
                    <div key={s} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/90">
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
